import { Button, Col, Form, message, Row, Select } from 'antd';
import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import type {
  ReducedStudyLine,
  ReducedUniversity,
  UniversityMemberInviteDto,
} from '../Types/interfaces';
import {
  GetStudyLinesOfUniversity,
  GetUniversities,
  InviteMembersToUniversity,
  InviteSupervisorsToStudyLine,
} from '../helper/apiHelper';
import { validateMultiEmail } from '../Validators/ValidatorEmail';

export const UniversityAddMemberComponent = (): ReactNode => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [universities, setUniversities] = useState<ReducedUniversity[]>([]);
  const [selectedUniversity, setSelectedUniversity] =
    useState<ReducedUniversity>();
  const [studyLines, setStudyLines] = useState<ReducedStudyLine[]>([]);
  const [selectedStudyLine, setSelectedStudyLine] =
    useState<ReducedStudyLine>();
  const { t } = useTranslation();

  const getUniverities = useCallback(async () => {
    try {
      const universityResp = await GetUniversities();
      setUniversities(universityResp);
      if (universityResp.length === 1) {
        setSelectedUniversity(universityResp[0]);
        form.setFieldValue('selectUniversity', universityResp[0].id);
      }
    } catch (error) {
      console.log(error);
      messageApi.error(t('univMemeberManComp.fetchUnisError'));
    }
  }, [messageApi, t, form]);

  const getStudyLines = useCallback(async () => {
    try {
      if (!selectedUniversity) {
        return;
      }
      const resp = await GetStudyLinesOfUniversity(
        selectedUniversity.id.toString()
      );
      setStudyLines(resp);

      if (resp.length === 1) {
        setSelectedStudyLine(resp[0]);
        form.setFieldValue('selectStudyLine', resp[0].id);
      }
    } catch (error) {
      console.log(error);
      messageApi.error(t('univMemeberManComp.fetchUnisError'));
    }
  }, [selectedUniversity, messageApi, t, form]);

  const clearFormFields = (fields?: string[]): void => {
    form.resetFields(fields);
  };

  const inviteMembersToStudyLine = async (): Promise<void> => {
    try {
      if (!selectedUniversity) {
        messageApi.info(t('univMemeberManComp.selUniFirst'));
        return;
      }

      const rectorEmails = form.getFieldValue('rectorEmails');
      const legalDepartmentMemberEmails = form.getFieldValue(
        'legalDepartmentMemberEmails'
      );
      const supervisorEmails = form.getFieldValue('supervisorEmails');

      if (supervisorEmails?.length && !selectedStudyLine) {
        messageApi.info(t('univMemeberManComp.selStudyLineFirst'));
        return;
      }

      // the rectors and legal department members belong to the university
      if (rectorEmails?.length || legalDepartmentMemberEmails?.length) {
        const universityMemberInviteDto: UniversityMemberInviteDto = {
          studyLineId: 0,
          rectorEmails: rectorEmails ? rectorEmails : [],
          legalDepartmentMemberEmails: legalDepartmentMemberEmails
            ? legalDepartmentMemberEmails
            : [],
          supervisorEmails: [],
        };

        await InviteMembersToUniversity(
          selectedUniversity.id.toString(),
          universityMemberInviteDto
        );
      }

      // the supervisors belong to the study line
      if (supervisorEmails?.length && selectedStudyLine) {
        await InviteSupervisorsToStudyLine(
          selectedStudyLine.id,
          supervisorEmails
        );
      }

      clearFormFields(['rectorEmails']);
      clearFormFields(['legalDepartmentMemberEmails']);
      clearFormFields(['supervisorEmails']);
      messageApi.success(t('univMemeberManComp.inviteSuccess'));
    } catch (error) {
      console.log(error);
      message.error(t('univMemeberManComp.inviteError'));
    }
  };

  const onUniversitySelect = (
    _value: unknown,
    option: { label: string; value: number }
  ): void => {
    setSelectedUniversity({
      id: option.value,
      name: option.label,
    });

    setSelectedStudyLine(undefined);
    form.setFieldValue('selectStudyLine', null);
  };

  const onStudyLineSelect = (
    _value: unknown,
    option: { label: string; value: number }
  ): void => {
    setSelectedStudyLine({
      id: option.value,
      name: option.label,
    });
  };

  return (
    <>
      {contextHolder}
      <Form form={form} size="middle">
        <Row>
          <Col span={24}>
            <Form.Item name="selectUniversity">
              <Select
                style={{ minWidth: '100%' }}
                showSearch
                allowClear
                placeholder={t('univMemeberManComp.selUni')}
                onClick={() => getUniverities()}
                options={universities?.map((uni) => ({
                  label: uni.name,
                  value: uni.id,
                }))}
                onSelect={onUniversitySelect}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="rectorEmails"
              rules={[{ validator: validateMultiEmail }]}
            >
              <Select
                mode="tags"
                style={{ minWidth: '100%' }}
                allowClear
                placeholder={t('univMemeberManComp.typeEmailToInviteRectors')}
                tokenSeparators={[',', ' ']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="legalDepartmentMemberEmails"
              rules={[{ validator: validateMultiEmail }]}
            >
              <Select
                mode="tags"
                style={{ minWidth: '100%' }}
                allowClear
                placeholder={t(
                  'univMemeberManComp.typeEmailToInviteLegalDepMem'
                )}
                tokenSeparators={[',', ' ']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item name="selectStudyLine">
              <Select
                style={{ minWidth: '100%' }}
                showSearch
                allowClear
                placeholder={t('univMemeberManComp.selStudyLine')}
                onClick={() => getStudyLines()}
                options={studyLines?.map((studyLine) => ({
                  label: studyLine.name,
                  value: studyLine.id,
                }))}
                onSelect={onStudyLineSelect}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="supervisorEmails"
              rules={[{ validator: validateMultiEmail }]}
            >
              <Select
                mode="tags"
                style={{ minWidth: '100%' }}
                allowClear
                placeholder={t(
                  'univMemeberManComp.typeEmailToInviteSupervisor'
                )}
                tokenSeparators={[',', ' ']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item>
              <Button
                type="primary"
                style={{ width: '100%' }}
                onClick={inviteMembersToStudyLine}
              >
                {t('univMemeberManComp.invToUni')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};
